import React from "react";
import { useQuery } from "@tanstack/react-query";
import { Refresh, Activity, Users, AlertTriangle } from "@/components/icons";

import { fetchClientVersionStats } from "@/lib/api";
import type { ClientVersionRow } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Stat } from "@/components/Stat";
import { Card, CardContent, CardTitle } from "@/components/ui/card";

function fmtInt(n: number): string {
  return n.toLocaleString();
}

function platformBadge(platform: string): string {
  switch (platform) {
    case "ios":
      return "bg-info/20 text-info";
    case "android":
      return "bg-success/20 text-success";
    default:
      return "bg-warning/20 text-warning";
  }
}

export function MobileVersionsPage() {
  const q = useQuery({
    queryKey: ["mobile", "versions"],
    queryFn: () => fetchClientVersionStats(30),
    refetchInterval: 15_000,
  });

  const data = q.data;
  const rows: ClientVersionRow[] = data?.active_distribution ?? [];
  const totalUsers = rows.reduce((s, r) => s + r.users, 0);
  const platforms = new Set(rows.map((r) => r.platform)).size;
  const collector = data?.collector ?? null;

  // Dropped telemetry means the request buffer overflowed between flushes — the
  // distribution is undercounting. Worth surfacing.
  const dropping = !!collector && collector.dropped > 0;

  return (
    <div className="space-y-5 pt-[86px]">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Mobile App Versions</h1>
          <div className="text-sm opacity-70">
            Which app versions are in the field — unique users by platform and version, seen
            in the last {data?.active_window_days ?? 30} days. Use this to decide when a
            minimum version is safe to enforce.
          </div>
        </div>
        <Button variant="ghost" size="sm" onClick={() => q.refetch()}>
          <Refresh className="h-4 w-4" /> Refresh
        </Button>
      </div>

      {q.isError ? (
        <div className="rounded-md border border-destructive/50 bg-destructive/10 p-4 text-destructive">
          Failed to load mobile version stats
        </div>
      ) : (
        <>
          {dropping && (
            <div className="flex items-start gap-2 rounded-md border border-warning/50 bg-warning/10 p-3 text-sm text-warning">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>
                Telemetry buffer overflowed ({fmtInt(collector!.dropped)} records dropped) —
                counts below may be undercounting. Consider raising MOBILE_STATS_MAX_BUFFER or
                shortening the flush interval.
              </span>
            </div>
          )}

          <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-4">
            <Stat
              title="Active users"
              value={fmtInt(totalUsers)}
              icon={<Users className="h-4 w-4 opacity-60" />}
              description={`across ${platforms} platform${platforms === 1 ? "" : "s"}`}
            />
            <Stat title="Distinct versions" value={fmtInt(rows.length)} />
            <Stat
              title="Buffered"
              value={fmtInt(collector?.buffered ?? 0)}
              description="not yet flushed"
            />
            <Stat
              title="Dropped"
              value={fmtInt(collector?.dropped ?? 0)}
              description="buffer overflow"
            />
          </div>

          <Card>
            <CardContent className="space-y-4 py-5">
              <CardTitle className="flex items-center gap-2 text-sm">
                <Activity className="h-4 w-4" /> Version distribution
              </CardTitle>

              {rows.length === 0 ? (
                <div className="text-sm opacity-60">
                  No mobile version data recorded yet. The app must send
                  <code className="mx-1 rounded bg-muted px-1">X-Client-Version</code>
                  and
                  <code className="mx-1 rounded bg-muted px-1">X-Client-Platform</code>
                  headers; data flushes roughly every 10 minutes.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border text-left opacity-60">
                        <th className="py-2 pr-4 font-medium">Platform</th>
                        <th className="py-2 pr-4 font-medium">Version</th>
                        <th className="py-2 pr-4 text-right font-medium">Users</th>
                        <th className="py-2 font-medium">Share</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((r) => {
                        const pct = totalUsers ? (r.users / totalUsers) * 100 : 0;
                        return (
                          <tr key={`${r.platform}:${r.app_version}`} className="border-b border-border/50">
                            <td className="py-2 pr-4">
                              <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${platformBadge(r.platform)}`}>
                                {r.platform}
                              </span>
                            </td>
                            <td className="py-2 pr-4 font-mono">{r.app_version}</td>
                            <td className="py-2 pr-4 text-right tabular-nums">{fmtInt(r.users)}</td>
                            <td className="py-2">
                              <div className="flex items-center gap-2">
                                <div className="h-2 w-32 overflow-hidden rounded-full bg-muted">
                                  <div className="h-full bg-primary/70" style={{ width: `${pct}%` }} />
                                </div>
                                <span className="text-xs opacity-70">{pct.toFixed(1)}%</span>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              <dl className="grid grid-cols-2 gap-x-8 gap-y-2 border-t border-border pt-4 text-sm sm:grid-cols-4">
                <div>
                  <dt className="opacity-60">Window</dt>
                  <dd className="font-mono">{data?.active_window_days ?? 30}d</dd>
                </div>
                <div>
                  <dt className="opacity-60">Last flush rows</dt>
                  <dd className="font-mono">{collector ? fmtInt(collector.last_flush_rows) : "—"}</dd>
                </div>
                <div>
                  <dt className="opacity-60">Flushes ok / failed</dt>
                  <dd className="font-mono">
                    {collector ? `${collector.flushes_ok} / ${collector.flushes_failed}` : "—"}
                  </dd>
                </div>
                <div>
                  <dt className="opacity-60">Auto-refresh</dt>
                  <dd className="font-mono">15s</dd>
                </div>
              </dl>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
